import { Trans, useLingui } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Table } from '@startuptools/ui/Table';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { GqlWarrantProgram } from '../../../../../graphql/base-types.graphql';
import { useNavigate } from '../../../../../components/Navigate';
import { Link } from 'react-router-dom';
import { wpEditOptionsSubsLink } from '../../../../../components/Links';

export const QesoStepSummary = ({
  warrantProgram,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    | 'id'
    | 'companyId'
    | 'name'
    | 'description'
    | 'maxNumberOfOptions'
    | 'strikePrice'
    | 'warrantStartDate'
    | 'shareSubscribeStartDate'
    | 'shareSubscribeEndDate'
    | 'warrantKind'
    | 'usesStepper'
  >;
}) => {
  const { i18n } = useLingui();
  const { link } = useNavigate();

  return (
    <Stack gap={3}>
      <Typography level="h4">
        <Trans id="Summary" />
      </Typography>
      <Alert>
        <Trans id="Here is a summary. Before you proceed, you should verify that everything is correct" />
      </Alert>

      <Table sx={{ tableLayout: 'auto', maxWidth: 700 }}>
        <tbody>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="The program's name" />:
              </Typography>
            </td>
            <td>{warrantProgram.name}</td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="The program's description" />:
              </Typography>
            </td>
            <td>{warrantProgram.description}</td>
          </tr>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Max number of options" />:
              </Typography>
            </td>
            <td>
              <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                {warrantProgram.maxNumberOfOptions}
              </NumberFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Strike-price per share" />:
              </Typography>
            </td>
            <td>
              <NumberFormat
                locale={i18n.locale}
                variant="currency"
                currency={'SEK'}
                minimumFractionDigits={2}
                maximumFractionDigits={6}
              >
                {warrantProgram.strikePrice}
              </NumberFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="The program starts" />:
              </Typography>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{warrantProgram.warrantStartDate}</DateFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="First day to subscribe for shares" />:
              </Typography>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{warrantProgram.shareSubscribeStartDate}</DateFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Last day to subscribe for shares" />:
              </Typography>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{warrantProgram.shareSubscribeEndDate}</DateFormat>
            </td>
          </tr>
        </tbody>
      </Table>

      <Box>
        <Button component={Link} to={link(wpEditOptionsSubsLink(warrantProgram))}>
          <Trans id="Continue" />
        </Button>
      </Box>
    </Stack>
  );
};
